import { Property } from '@shared/definitions/property';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Properties whose payment grace period is still running: payment setup is
 * required, an end date exists and it is still in the future. A property past
 * its grace end is no longer a "will be deleted in N days" warning.
 */
export const filterGracePeriodProperties = (
  properties: readonly Property[],
  now: Date
): Property[] =>
  properties.filter(p =>
    p.requiresPaymentSetup &&
    p.billingGracePeriodEnds &&
    p.billingGracePeriodEnds > now
  );

/** Smallest number of whole days remaining across the grace-period properties. */
export const minGracePeriodDays = (
  gracePeriodProperties: Property[],
  now: Date
): number => {
  if (gracePeriodProperties.length === 0) return 0;
  return Math.min(
    ...gracePeriodProperties.map(p => {
      if (!p.billingGracePeriodEnds) return 999;
      const diff = p.billingGracePeriodEnds.getTime() - now.getTime();
      return Math.ceil(diff / MS_PER_DAY);
    })
  );
};

/**
 * Pure i18n descriptor for the grace-period message: returns the translation
 * key and its interpolation params, so the rendering layer only has to call
 * `translate.instant(key, params)`. Keeps the singular/plural × today/tomorrow/
 * days matrix testable without a TranslateService.
 */
export interface GracePeriodMessage {
  key: string;
  params: { count: number; days: number };
}

export const gracePeriodMessage = (
  gracePeriodProperties: Property[],
  now: Date
): GracePeriodMessage => {
  const count = gracePeriodProperties.length;
  const days = minGracePeriodDays(gracePeriodProperties, now);
  const base = 'dashboard.property.alerts.paymentRequired';
  const params = { count, days };

  if (count === 1) {
    if (days === 0) return { key: `${base}.msgTodaySingular`, params };
    if (days === 1) return { key: `${base}.msgTomorrowSingular`, params };
    return { key: `${base}.msgDaysSingular`, params };
  }

  if (days === 0) return { key: `${base}.msgTodayPlural`, params };
  if (days === 1) return { key: `${base}.msgTomorrowPlural`, params };
  return { key: `${base}.msgDaysPlural`, params };
};
